import clsx from 'clsx';
import Icon from '../icon/icon';
import Textarea, { TextareaProps } from '../textarea/textarea';

/* eslint-disable-next-line */
type BG_BUTTON_TYPES = 'primary' | 'base' | 'baseError' | 'transparent' | 'error';

const BG_BUTTON_CLASSES = {
    primary: 'bg-primary active:bg-primary-50 active:text-primary disabled:bg-primary-50 disabled:text-primary',
    base: 'bg-gray-50 group-focus-within:bg-primary-50',
    transparent: 'bg-transparent',
    error: 'bg-secondary active:bg-secondary-50 active:text-secondary disabled:bg-secondary-50 disabled:text-secondary',
    baseError: 'bg-gray-50 group-focus-within:bg-secondary-50 ',
};

const ICON_BASE_CLASSES = {
    primary: 'text-white group-one-active:text-primary group-one-disabled:text-primary',
    base: 'text-gray-900 group-focus-within:text-primary',
    transparent: 'text-stone-500',
    error: 'text-white group-one-active:text-secondary group-one-disabled:text-secondary',
    baseError: 'text-gray-900 group-focus-within:text-secondary',
};

const COMMON_BUTTON_CLASSES = 'absolute top-[1px] bottom-[1px] flex justify-center items-center';

export interface TextAreaProps extends TextareaProps {
    label: string;
    helper?: string;
    startButton?: {
        iconName: string;
        background: BG_BUTTON_TYPES;
    };
    endButton?: {
        iconName?: string;
        background: BG_BUTTON_TYPES;
        position?: 'left' | 'right';
        label?: string;
    };
    required?: boolean;
    buttonDisabled?: boolean;
    testId?: string;
}

export function TextArea(props: TextAreaProps) {
    const endIcon = props?.endButton?.label ? (
        <>
            <p className={props.endButton?.position === 'left' ? 'pr-sm' : 'pl-sm'}>{props?.endButton?.label}</p>
            {props?.endButton?.iconName && (
                <Icon
                    className={
                        ICON_BASE_CLASSES[
                            props.error
                                ? props.endButton?.background === 'primary'
                                    ? 'error'
                                    : 'baseError'
                                : props?.endButton?.background || 'base'
                        ]
                    }
                    name={props?.endButton?.iconName}
                    size={14}
                />
            )}
        </>
    ) : (
        <Icon
            className={
                ICON_BASE_CLASSES[
                    props.error
                        ? props.endButton?.background === 'primary'
                            ? 'error'
                            : 'baseError'
                        : props?.endButton?.background || 'base'
                ]
            }
            name={props?.endButton?.iconName}
            size={14}
        />
    );

    const startButtonClasses = clsx(
        COMMON_BUTTON_CLASSES,
        BG_BUTTON_CLASSES[props.error ? 'baseError' : props.startButton?.background || 'base'],
        'left-[1px] px-3 rounded-l',
    );

    const endButtonClasses = clsx(
        'group-one',
        COMMON_BUTTON_CLASSES,
        BG_BUTTON_CLASSES[
            props.error
                ? props.endButton?.background === 'primary'
                    ? 'error'
                    : 'baseError'
                : props?.endButton?.background || 'base'
        ],
        props?.endButton?.label ? 'px-4 text-white text-sm font-semibold font-poppins' : 'px-3',
        props.endButton?.position === 'left' ? 'flex-row' : 'flex-row-reverse',
        'right-[1px] rounded-r',
    );

    const inputCustomClasses = clsx(props?.startButton?.iconName ? 'pl-11' : 'pl-sm', props?.className);

    return (
        <div className="flex flex-col-reverse">
            <p className="text-xs text-gray-300 mt-1 font-normal">{props.helper}</p>
            <div className="relative group">
                {props?.startButton?.iconName && (
                    <button className={startButtonClasses} type="button" disabled={props.buttonDisabled}>
                        <Icon
                            className={
                                ICON_BASE_CLASSES[props.error ? 'baseError' : props?.startButton?.background || 'base']
                            }
                            name={props?.startButton?.iconName}
                            size={14}
                        />
                    </button>
                )}
                <Textarea
                    id={props.id}
                    name={props.name}
                    value={props.value}
                    rounded={props.rounded}
                    error={props.error}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    onClick={props.onClick}
                    onBlur={props.onBlur}
                    onChange={props.onChange}
                    onFocus={props.onFocus}
                    className={inputCustomClasses}
                    size={props.size}
                    data-test-id={props.testId}
                    data-testid={props.testId}
                />
                {props.endButton && (
                    <button className={endButtonClasses} type="button" disabled={props.buttonDisabled}>
                        {endIcon}
                    </button>
                )}
            </div>
            <label className="text-sm text-black font-medium mb-1">
                {props.label}
                {props.required && <Icon name="ri-asterisk" className="ml-1 text-error-100" size={10} />}
            </label>
        </div>
    );
}

TextArea.defaultProps = {
    label: '',
};

export default TextArea;
